import { Fragment, useEffect, useRef, useState } from 'react'
import type { KeyboardEvent } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'
import { buildSlotMap, isOccupiable, rowLabel } from '@/lib/storageTree'
import { nextCell, type ArrowKey, type GridPosition } from '@/lib/slotMapNav'
import {
  cellWidthPx,
  fillOf,
  fillTone,
  gridSizeFor,
  mapWidthBounds,
  FIT_FLOOR_PX,
  showsCode,
  showsCodeAt,
  type CellSize,
  type ContainerFill,
  type FillIndex,
} from '@/lib/slotMetrics'
import type { ApiStorageNode, LocateResult, SlotState } from '@/types/storage'
import { useLocationText } from '@/hooks/useStoragePlaces'

const ARROW_KEYS: ReadonlySet<string> = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
])

function isArrowKey(key: string): key is ArrowKey {
  return ARROW_KEYS.has(key)
}

const posKey = (pos: GridPosition): string => `${pos.row}:${pos.column}`

/** The first real cell in the grid, 1-based — the roving tab stop's default. */
function firstCellPos(grid: ReadonlyArray<ReadonlyArray<unknown>>): GridPosition {
  for (let r = 0; r < grid.length; r += 1) {
    for (let c = 0; c < grid[r].length; c += 1) {
      if (grid[r][c]) return { row: r + 1, column: c + 1 }
    }
  }
  return { row: 1, column: 1 }
}

/**
 * Three visually distinct fills. Blocked must never read as merely "in use":
 * a blocked slot is one nobody can put anything into, and confusing the two is
 * how a capacity figure stops being trusted.
 */
const SLOT_STYLES: Record<SlotState, string> = {
  free: 'border-emerald-200 bg-emerald-50/70 text-emerald-900 hover:bg-emerald-100 hover:border-emerald-300',
  occupied: 'border-sky-300 bg-sky-100 text-sky-900 hover:bg-sky-200',
  blocked:
    'border-zinc-300 bg-[repeating-linear-gradient(45deg,theme(colors.zinc.100)_0_5px,theme(colors.zinc.300)_5px_10px)] text-zinc-500',
}

const SLOT_LABELS: Record<SlotState, string> = {
  free: 'Free',
  occupied: 'Has item',
  blocked: 'Blocked',
}

/** Legend order: what is there, what is open, what is out of service. */
const LEGEND_ORDER: SlotState[] = ['occupied', 'free', 'blocked']

/**
 * A layout-only map (Equipment) is drawn without knowing what is stored, so a
 * slot that is not blocked is "In service" — calling it "Free" would claim it
 * is empty, which that map has not checked.
 */
const stateLabel = (state: SlotState, layoutOnly: boolean): string =>
  layoutOnly && state === 'free' ? 'In service' : SLOT_LABELS[state]

/**
 * The row-label gutter and the grid gap, in px.
 *
 * Constants rather than Tailwind classes because the same two numbers lay the
 * grid out AND feed `cellWidthPx` below, which works out how wide a cell will
 * end up so the code/no-code decision can be made in the same pass. As classes
 * they would be a `gap-1.5` here and a `6` there, free to drift apart.
 */
const GUTTER_PX = 24
const GAP_PX = 6

/**
 * Cell geometry per density band.
 *
 * `maxPx` ceilings a column; every band shares one floor, `FIT_FLOOR_PX`, so a
 * row shrinks to fit its card rather than pushing its last cell out of view
 * (a cell under its band's code width just stops printing the code). The grid
 * is handed the full width of its card (see `w-full` below) and every column
 * grows equally up to that ceiling, so a 2×5 of racks uses the room it is in
 * instead of hugging the left edge of a 1000px card. The ceiling is what stops
 * the opposite failure: a 1×2 grid would otherwise mint two cells the size of a
 * dinner plate.
 *
 * Height follows width (`aspectRatio: 1`) and is clamped by the same ceiling, so
 * cells grow as squares instead of stretching into letterboxes — a slot map has
 * to read as a physical shelf, and a 190×56 rectangle does not.
 *
 * One number per bound, applied as inline style rather than as a `min-h-*`
 * class beside it: the floor and the ceiling are read by the arithmetic too, and
 * a class that disagreed with the number would put the code/no-code decision
 * out of step with the cell it was deciding about.
 *
 * `text` is the other half of `CODE_FITS_FROM_PX`, whose thresholds were
 * measured at exactly these three sizes — 14px, 12px, 10px. Change one and the
 * other has to be re-measured, or a cell will print a code it cannot fit.
 *
 * `sub` is the lot number beneath the code. 11px is not a free choice either: a
 * real lot number here runs to 17 characters (`CONS-GLOVE-004-L1`), which
 * measures 116.3px at 11px and so needs a 127px cell — one pixel inside the
 * `lg` ceiling. 12px would need 136px and could never fit. `sm` stays at 10px
 * because its own code is 10px, and a lot number set larger than the code it
 * hangs under reads as the heading.
 */
const SIZE_STYLES: Record<CellSize, { maxPx: number; text: string; sub: string }> = {
  lg: { maxPx: 128, text: 'text-sm', sub: 'text-[11px]' },
  md: { maxPx: 96, text: 'text-xs', sub: 'text-[11px]' },
  sm: { maxPx: 64, text: 'text-[10px]', sub: 'text-[10px]' },
}

/** Every cell in a band is laid out by exactly these three properties. */
/**
 * A band's geometry, with its ceiling raised to `maxCellPx` when the caller
 * gives the map room to grow — cells stay square and fill the card instead of
 * leaving a wide empty strip beside a small grid.
 */
const sizedGeom = (size: CellSize, maxCellPx?: number) =>
  maxCellPx && maxCellPx > SIZE_STYLES[size].maxPx
    ? { ...SIZE_STYLES[size], maxPx: maxCellPx }
    : SIZE_STYLES[size]

// Square, and no taller than the band allows. The floor is the fit floor, not
// the band's: a cell squeezed to fit its card stays square rather than
// stretching into a tall bar.
const cellBox = (geom: { maxPx: number }) => ({
  aspectRatio: '1',
  minHeight: FIT_FLOOR_PX,
  maxHeight: geom.maxPx,
})

const TONE_BAR: Record<ReturnType<typeof fillTone>, string> = {
  empty: 'bg-muted',
  some: 'bg-emerald-400',
  most: 'bg-amber-400',
  full: 'bg-rose-400',
}

export function SlotMapLegend({ layoutOnly = false }: { layoutOnly?: boolean } = {}) {
  const states: SlotState[] = layoutOnly ? ['free', 'blocked'] : LEGEND_ORDER
  return (
    <div className='flex flex-wrap items-center gap-4 text-xs text-muted-foreground'>
      {states.map((state) => (
        <span key={state} className='flex items-center gap-1.5'>
          <span className={cn('inline-block h-3.5 w-3.5 rounded border', SLOT_STYLES[state])} />
          {stateLabel(state, layoutOnly)}
        </span>
      ))}
    </div>
  )
}

type SlotMapProps = {
  /** The node that declares the grid (a unit, rack or box). */
  parent: ApiStorageNode
  /** Its grid children — positions, or racks/boxes for a container grid. */
  positions: ApiStorageNode[]
  occupiedIds: ReadonlySet<string>
  onSelect?: (node: ApiStorageNode) => void
  selectedId?: string | null
  /**
   * How full each container cell is, worked out ONCE by the caller.
   *
   * This used to be the whole subtree, and each container cell scanned it to
   * count its own slots. Drawing one rack of a hundred boxes therefore made a
   * hundred passes over the freezer — 276 ms of arithmetic per render on a
   * million-slot unit, repeated on every hover and selection. The figures come
   * from the server with the tree now, so a cell looks its own up.
   *
   * Without it, container cells render as plain cells rather than guessing.
   */
  fills?: FillIndex
  /** Lots by the id of the slot holding them, for the hover card. */
  lotsByNodeId?: ReadonlyMap<string, LocateResult[]>
  /** Drawn without contents (Equipment): slots read as in service or blocked. */
  layoutOnly?: boolean
  /** Let cells grow past their band's ceiling, up to this, to fill the card. */
  maxCellPx?: number
}

export function SlotMap({
  parent,
  positions,
  occupiedIds,
  onSelect,
  selectedId,
  fills,
  lotsByNodeId,
  layoutOnly = false,
  maxCellPx,
}: SlotMapProps) {
  // How much width the matrix was actually given. Declared before the early
  // return below so the hook order never depends on whether there is a grid.
  //
  // The band alone cannot answer whether a code is legible: it is chosen from
  // the CELL COUNT, and the same 9×9 box is a wall of tiny squares in a 260px
  // column and a readable map in a full-width card. Observing the scroller —
  // whose content width is what the grid's `w-full` resolves against — is what
  // lets the answer follow the container.
  const scrollerRef = useRef<HTMLDivElement>(null)
  const [containerPx, setContainerPx] = useState(0)

  useEffect(() => {
    const el = scrollerRef.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setContainerPx(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  // Roving tabIndex: exactly one cell is a Tab stop, and Arrow keys move
  // which one that is. Declared unconditionally (hooks can't follow the
  // empty-grid return below) even though it is only read once a grid exists.
  const cellRefs = useRef(new Map<string, HTMLButtonElement>())
  const [focusPos, setFocusPos] = useState<GridPosition | null>(null)
  const registerCellRef = (pos: GridPosition, el: HTMLButtonElement | null) => {
    const key = posKey(pos)
    if (el) cellRefs.current.set(key, el)
    else cellRefs.current.delete(key)
  }

  const grid = buildSlotMap(parent, positions, occupiedIds)

  if (grid.length === 0) {
    return (
      <p className='text-sm text-muted-foreground'>
        Nothing is inside {parent.code} yet. Describe what it holds and the slots are created and
        named for you — capacity is counted from them, never typed in.
      </p>
    )
  }

  const rows = parent.gridRows ?? 0
  const columns = parent.gridColumns ?? 0
  // Sized for what it holds, not just how many cells it has — see `gridSizeFor`.
  const size = gridSizeFor(
    (parent.gridRows ?? 0) * columns,
    positions.some((p) => (lotsByNodeId?.get(p.id)?.length ?? 0) > 0),
  )
  const geom = sizedGeom(size, maxCellPx)
  // Columns share the width equally and stop at the band's ceiling; the grid
  // is never narrower than every column at the fit floor, which is the only
  // case where the card scrolls.
  const bounds = mapWidthBounds({
    columns,
    gutterPx: GUTTER_PX,
    gapPx: GAP_PX,
    floorPx: FIT_FLOOR_PX,
    maxPx: geom.maxPx,
  })

  // Until the first measurement lands, fall back to the band heuristic rather
  // than to the floor `cellWidthPx` reports for an unmeasured grid — otherwise
  // every roomy grid renders wordless for a frame and then fills in.
  const showCode =
    containerPx === 0
      ? showsCode(size)
      : showsCodeAt(
          size,
          cellWidthPx({
            containerPx,
            columns,
            gutterPx: GUTTER_PX,
            gapPx: GAP_PX,
            minPx: FIT_FLOOR_PX,
            maxPx: geom.maxPx,
          }),
        )

  const gridSize = { rows, columns }

  // The roving Tab stop. Falls back to the first real cell whenever
  // `focusPos` is unset or has gone stale (e.g. the grid shrank), so there is
  // always exactly one focusable cell and never zero.
  const activePos =
    focusPos && grid[focusPos.row - 1]?.[focusPos.column - 1] ? focusPos : firstCellPos(grid)

  const moveFocus = (pos: GridPosition) => {
    if (!grid[pos.row - 1]?.[pos.column - 1]) return
    setFocusPos(pos)
    cellRefs.current.get(posKey(pos))?.focus()
  }

  return (
    // `min-w-0` so the card, not the page, is what scrolls; the padding keeps
    // a selected edge cell's ring inside the scroller, which clips it.
    <div ref={scrollerRef} className='min-w-0 overflow-x-auto p-1'>
      {/* ONE grid for the whole matrix — headers, row gutter and every cell.
          Each row used to be its own grid container, which meant the column
          tracks were sized per row independently: rows whose cells carried
          longer text laid out differently from rows that did not, so the
          columns stopped lining up as soon as cells said anything but their
          code. Sharing one set of tracks is what guarantees every cell is the
          same size, occupied or not. */}
      <div
        // `w-full`, not `inline-grid`: shrink-to-fit sized every column at its
        // floor, so the matrix sat in the corner of the card however much room
        // it had. Equal `1fr` columns fill the card up to `maxWidth` (every
        // cell at its ceiling); `minWidth` (every cell at the fit floor) is
        // the only point where the wrapper's `overflow-x-auto` scrolls.
        className='grid w-full'
        style={{
          gridTemplateColumns: `${GUTTER_PX}px repeat(${columns}, minmax(0, 1fr))`,
          gap: GAP_PX,
          minWidth: bounds.minPx,
          maxWidth: bounds.maxPx,
        }}
      >
        {/* Corner above the row gutter. */}
        <div aria-hidden />
        {Array.from({ length: columns }, (_, c) => (
          <div key={c} className='text-center text-[11px] font-medium text-muted-foreground'>
            {c + 1}
          </div>
        ))}

        {grid.map((row, r) => (
          <Fragment key={r}>
            <div className='flex items-center justify-end text-[11px] font-medium text-muted-foreground'>
              {/* SEQUENTIAL labels carry no row letter, so the gutter shows the
                  row number instead of implying an A/B/C the codes don't use. */}
              {parent.labelScheme === 'SEQUENTIAL' ? r + 1 : rowLabel(r)}
            </div>
            {row.map((cell, c) =>
              cell ? (
                <Cell
                  key={cell.node.id}
                  node={cell.node}
                  state={cell.state}
                  size={size}
                  showCode={showCode}
                  selected={selectedId === cell.node.id}
                  onSelect={onSelect}
                  fills={fills}
                  lots={lotsByNodeId?.get(cell.node.id) ?? []}
                  pos={{ row: r + 1, column: c + 1 }}
                  gridSize={gridSize}
                  focused={activePos.row === r + 1 && activePos.column === c + 1}
                  onMove={moveFocus}
                  registerRef={registerCellRef}
                  layoutOnly={layoutOnly}
                  maxCellPx={maxCellPx}
                />
              ) : (
                // A declared cell with no row behind it. Shown as a gap
                // rather than reflowed away, so the discrepancy is visible.
                <div
                  key={`missing-${r}-${c}`}
                  title='Declared by the grid, but no row exists for it'
                  style={cellBox(geom)}
                  className={cn(
                    'flex items-center justify-center rounded border border-dashed border-destructive/40 text-destructive/70',
                    geom.text,
                  )}
                >
                  ?
                </div>
              ),
            )}
          </Fragment>
        ))}
      </div>
    </div>
  )
}

/**
 * One cell.
 *
 * A container (rack, box) shows how full it is so the level above answers
 * "where is there room" without opening every child. A slot shows its state
 * and, on hover, what is actually in it.
 */
function Cell({
  node,
  state,
  size,
  showCode,
  selected,
  onSelect,
  fills,
  lots,
  pos,
  gridSize,
  focused,
  onMove,
  registerRef,
  layoutOnly,
  maxCellPx,
}: {
  node: ApiStorageNode
  state: SlotState
  size: CellSize
  /** Decided once for the whole matrix from its measured width, not per cell. */
  showCode: boolean
  selected: boolean
  onSelect?: (node: ApiStorageNode) => void
  fills?: FillIndex
  lots: LocateResult[]
  /** This cell's 1-based place in the declared grid. */
  pos: GridPosition
  gridSize: { rows: number; columns: number }
  /** Whether this is the matrix's one roving Tab stop. */
  focused: boolean
  onMove: (pos: GridPosition) => void
  registerRef: (pos: GridPosition, el: HTMLButtonElement | null) => void
  layoutOnly: boolean
  maxCellPx?: number
}) {
  const geom = sizedGeom(size, maxCellPx)
  const isSlot = isOccupiable(node.kind)
  // One lookup, not a scan. See the `fills` prop.
  const fill = !isSlot && fills ? fillOf(fills, node.id) : null

  // Anchor for the portalled hover card. The grid scrolls horizontally, and
  // `overflow-x: auto` forces the vertical axis to clip as well — so a card
  // rendered inside this element is cut off by the scroller no matter how it is
  // positioned. It has to leave the container entirely.
  const [hoverAt, setHoverAt] = useState<{ top: number; left: number } | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  const show = () => {
    const r = ref.current?.getBoundingClientRect()
    if (r) setHoverAt({ top: r.top, left: r.left + r.width / 2 })
  }

  // Arrow keys move the roving Tab stop; Enter/Space select via the button's
  // own native activation, so they need no handling here.
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!isArrowKey(e.key)) return
    e.preventDefault()
    onMove(nextCell(pos, e.key, gridSize))
  }

  // "<code>, <lot number | 'free' | 'blocked'>" for a slot — the truncated
  // label in the cell is the one thing a screen reader cannot recover from
  // the colour or the stripe. A container keeps its fill-based label, since
  // "free"/"blocked" describe a slot's state, not how full a rack is.
  const ariaLabel = fill
    ? `${node.code}, ${fill.used} of ${fill.usable} slots in use`
    : lots.length > 0
      ? `${node.code}, ${lots.map((l) => l.lotNumber).join(', ')}`
      : `${node.code}, ${stateLabel(state, layoutOnly).toLowerCase()}`

  return (
    <div
      ref={ref}
      // `min-w-0` so a long lot number is truncated by the cell rather than
      // widening its grid column — which would make one cell bigger than the
      // rest for no reason but its contents.
      className='group relative min-w-0'
      onMouseEnter={show}
      onFocus={show}
      onMouseLeave={() => setHoverAt(null)}
      onBlur={() => setHoverAt(null)}
    >
      <button
        ref={(el) => registerRef(pos, el)}
        type='button'
        tabIndex={focused ? 0 : -1}
        onClick={() => onSelect?.(node)}
        onKeyDown={handleKeyDown}
        aria-label={ariaLabel}
        style={cellBox(geom)}
        className={cn(
          'w-full rounded border px-1 font-medium transition-colors',
          geom.text,
          fill ? 'border-border bg-card hover:bg-muted/60' : SLOT_STYLES[state],
          selected && 'ring-2 ring-primary ring-offset-1',
        )}
      >
        {fill ? (
          <span className='flex h-full flex-col items-center justify-center gap-1'>
            <span className='truncate'>{node.code}</span>
            <span className='h-1 w-full overflow-hidden rounded-full bg-muted'>
              <span
                className={cn('block h-full rounded-full', TONE_BAR[fillTone(fill.pct)])}
                style={{ width: `${fill.pct}%` }}
              />
            </span>
            <span className='text-[10px] font-normal text-muted-foreground'>{fill.label}</span>
          </span>
        ) : showCode ? (
          // A slot holding stock says WHAT it holds. Colour alone answers
          // "is this taken"; the question someone actually walks up to the
          // freezer with is "where is that material", and answering it by
          // hovering eighty-one cells one at a time is not answering it.
          // The same wrapper whether or not the slot holds anything, so a full
          // cell and an empty one are laid out identically and only differ in
          // what they say.
          <span className='flex h-full w-full min-w-0 flex-col items-center justify-center leading-tight'>
            <span className='w-full truncate'>{node.code}</span>
            {/* The lot, not the material: two vials of the same product are
                the same material code and different lots, and the lot is what
                a release record, a recall or a chain-of-custody question is
                asked about. The material name is a hover away. */}
            {lots.length > 0 ? (
              <span className={cn('w-full truncate font-normal opacity-80', geom.sub)}>
                {lots[0].lotNumber}
                {lots.length > 1 && ` +${lots.length - 1}`}
              </span>
            ) : (
              // The stripe is the colour-independent signal for most people,
              // but it is still a visual pattern — this is the one that
              // survives being read aloud or greyscaled.
              state === 'blocked' && (
                <span className={cn('w-full truncate font-normal opacity-80', geom.sub)}>
                  Blocked
                </span>
              )
            )}
          </span>
        ) : (
          // Measured too narrow for the whole code, and half a code is worse
          // than none: state is carried by the fill alone, and the code appears
          // on hover.
          <span className='sr-only'>{node.code}</span>
        )}
      </button>

      {hoverAt && (
        <HoverCard node={node} state={state} fill={fill} lots={lots} at={hoverAt} layoutOnly={layoutOnly} />
      )}
    </div>
  )
}

/**
 * What is actually here, on hover.
 *
 * Rendered into a body portal with fixed positioning. The obvious approach —
 * an absolutely positioned child — is silently cropped by the grid's horizontal
 * scroller, because `overflow-x: auto` makes the vertical axis clip too. Only
 * leaving the container fixes it.
 *
 * Mounted per hovered cell rather than one per slot: an 81-slot box would
 * otherwise carry 81 always-present tooltips.
 */
function HoverCard({
  node,
  state,
  fill,
  lots,
  at,
  layoutOnly,
}: {
  node: ApiStorageNode
  state: SlotState
  fill: ContainerFill | null
  lots: LocateResult[]
  at: { top: number; left: number }
  layoutOnly: boolean
}) {
  const locate = useLocationText()
  // Flip below the cell when there is no room above it — the top row of a grid
  // sits close enough to the header that a card above would be off-screen.
  const flip = at.top < 140

  return createPortal(
    <div
      role='tooltip'
      style={{
        position: 'fixed',
        top: flip ? at.top + 44 : at.top - 8,
        left: at.left,
        transform: flip ? 'translate(-50%, 0)' : 'translate(-50%, -100%)',
      }}
      className='pointer-events-none z-[9999] w-max max-w-[16rem] rounded-md border bg-popover px-2.5 py-1.5 text-left text-xs shadow-md'
    >
      <p className='font-medium'>{node.code}</p>
      <p className='text-muted-foreground'>{locate(node.path)}</p>
      {fill ? (
        <p className='mt-1'>
          {fill.total === 0
            ? 'Nothing inside it yet'
            : `${fill.used} of ${fill.usable} slots in use${fill.blocked ? `, ${fill.blocked} blocked` : ''}`}
        </p>
      ) : (
        <p className='mt-1'>{stateLabel(state, layoutOnly)}</p>
      )}
      {lots.length > 0 && (
        <ul className='mt-1 space-y-0.5 border-t pt-1'>
          {lots.map((lot) => (
            <li key={lot.lotId}>
              <span className='font-medium'>{lot.lotNumber}</span>{' '}
              <span className='text-muted-foreground'>
                {lot.materialName} · {lot.quantity}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  )
}
